/**
 * @file `ReportIssueModal` — the in-lab "Report an issue" dialog (#1672).
 * @module @labcraft/lab/ui/components/report-issue-modal
 *
 * Opened from the app-bar report button. Lets a learner send actionable
 * feedback from the point of friction without leaving the lab. The fast
 * path is deliberately frictionless: pick nothing, type one sentence,
 * send — category and the attached context are optional refinements, not
 * gates (submit enables on a non-empty description).
 *
 * Submission is a seam: the caller passes `onSubmit(draft)` — this
 * component owns only the dialog state machine `idle → sending → sent |
 * failed`. Success shows an inline confirmation then auto-dismisses;
 * failure keeps the draft and offers retry. The support bundle itself is
 * assembled by the caller (from the reader chrome) and secret-redacted by
 * the lab daemon — the SPA composes only the learner's words + the attach
 * flag. Dismissal + motion gating mirror `SettingsModal` (Escape, ×,
 * scrim click; OS media query + `[data-reduce-motion]`).
 */

import { useEffect, useRef, useState, type JSX } from 'react'
import {
  REPORT_CATEGORY_LABELS,
  ReportCategorySchema,
  type ReportCategory,
} from '../../shared/routes.ts'
import styles from './report_issue_modal.module.css'

/**
 * What the modal hands to `onSubmit` — the learner's own words plus the
 * attach flag. The redacted support bundle is added by the caller and
 * scrubbed server-side; nothing here carries session context.
 *
 * @property category - The picked category, or `null` when none.
 * @property message - The learner's free-text description (non-empty).
 * @property attachContext - Whether to attach recent lab context.
 */
export type ReportDraft = {
  category: ReportCategory | null
  message: string
  attachContext: boolean
}

/** Category options in display order, from the shared label map. */
const CATEGORIES: ReadonlyArray<{ id: ReportCategory; label: string }> =
  ReportCategorySchema.options.map(id => ({
    id,
    label: REPORT_CATEGORY_LABELS[id],
  }))

/** Auto-dismiss delay after a successful send. */
const SENT_DISMISS_MS = 2200

/** Dialog state machine. */
type Phase = 'idle' | 'sending' | 'sent' | 'failed'

/**
 * Props for {@link ReportIssueModal}.
 *
 * @property whereLabel - "Where you are" chip text, e.g. "bash · Ch 3 / 12"
 *   (from reader chrome), or `null` outside the reader (the chip hides).
 * @property canAttachContext - Whether the "attach recent lab context"
 *   affordance is offered — true only in a reader with a terminal, where
 *   objective/VM/command context actually exists (#1672). When false the
 *   toggle + disclosure are hidden entirely and no reader context is sent
 *   (browser particulars still ride along, wired by the caller).
 * @property onSubmit - Perform the send. Resolve = sent; reject = the
 *   failure state with retry.
 * @property onClose - Dismiss the modal.
 */
export type ReportIssueModalProps = {
  whereLabel: string | null
  canAttachContext: boolean
  onSubmit: (draft: ReportDraft) => Promise<void>
  onClose: () => void
}

/**
 * The report-issue dialog. See the file header for the state machine and
 * the submission seam.
 *
 * @param props - {@link ReportIssueModalProps}.
 * @returns The modal element.
 */
export function ReportIssueModal(props: ReportIssueModalProps): JSX.Element {
  const cardRef = useRef<HTMLDivElement | null>(null)
  const [category, setCategory] = useState<ReportCategory | null>(null)
  const [message, setMessage] = useState('')
  const [attach, setAttach] = useState(props.canAttachContext)
  const [showIncluded, setShowIncluded] = useState(false)
  const [phase, setPhase] = useState<Phase>('idle')

  // Escape closes; Tab cycles within the card (focus trap).
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        props.onClose()
        return
      }
      if (e.key !== 'Tab' || !cardRef.current) return
      const focusables = cardRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), textarea:not([disabled])',
      )
      if (focusables.length === 0) return
      const first = focusables[0]!
      const last = focusables[focusables.length - 1]!
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [props])

  // Auto-dismiss after the inline success confirmation.
  useEffect(() => {
    if (phase !== 'sent') return
    const t = setTimeout(props.onClose, SENT_DISMISS_MS)
    return () => clearTimeout(t)
  }, [phase, props])

  const canSend = message.trim().length > 0 && phase !== 'sending'

  /** Send (or retry). Resolve → sent; reject → failed with draft kept. */
  async function send(): Promise<void> {
    if (!canSend) return
    setPhase('sending')
    try {
      await props.onSubmit({
        category,
        message: message.trim(),
        attachContext: props.canAttachContext && attach,
      })
      setPhase('sent')
    } catch {
      setPhase('failed')
    }
  }

  return (
    <div
      className={styles.scrim}
      role="dialog"
      aria-modal
      aria-labelledby="report-issue-title"
      onMouseDown={props.onClose}>
      <div
        className={styles.card}
        ref={cardRef}
        onMouseDown={e => e.stopPropagation()}>
        {phase === 'sent' ? (
          <div className={styles.okWrap}>
            <div className={styles.okBadge} aria-hidden>
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round">
                <path d="M5 13l5 5L20 7" />
              </svg>
            </div>
            <h2 className={styles.okTitle} id="report-issue-title">
              Report sent
            </h2>
            <p className={styles.okBody}>
              Thanks for flagging it. We may follow up by email if we need more
              detail.
            </p>
            <p className={styles.okHint}>Taking you back to your lab…</p>
          </div>
        ) : (
          <>
            <header className={styles.head}>
              <h2 className={styles.title} id="report-issue-title">
                Report an issue
              </h2>
              <button
                className={styles.closeBtn}
                type="button"
                aria-label="Close report"
                onClick={props.onClose}>
                ×
              </button>
            </header>
            <p className={styles.help}>
              Hit a snag? Send it from right here — you won&rsquo;t leave the
              lab.
            </p>

            <p className={styles.fieldLabel}>
              What kind of issue?{' '}
              <span className={styles.optional}>optional</span>
            </p>
            <div className={styles.catGroup} role="group" aria-label="Category">
              {CATEGORIES.map(c => (
                <button
                  key={c.id}
                  type="button"
                  className={styles.cat}
                  aria-pressed={category === c.id}
                  disabled={phase === 'sending'}
                  onClick={() => setCategory(category === c.id ? null : c.id)}>
                  {c.label}
                </button>
              ))}
            </div>

            <p className={styles.fieldLabel}>What happened?</p>
            <textarea
              className={styles.textarea}
              rows={3}
              maxLength={2000}
              autoFocus
              disabled={phase === 'sending'}
              placeholder="What did you expect, and what happened instead?"
              value={message}
              onChange={e => setMessage(e.target.value)}
            />

            {props.canAttachContext && (
              <>
                <div className={styles.ctxRow}>
                  <div className={styles.ctxText}>
                    <p className={styles.ctxTitle}>Attach recent lab context</p>
                    <p className={styles.ctxHint}>Helps us see what you saw</p>
                  </div>
                  <button
                    type="button"
                    className={
                      attach
                        ? `${styles.toggle} ${styles.toggleOn}`
                        : styles.toggle
                    }
                    role="switch"
                    aria-checked={attach}
                    aria-label="Attach recent lab context"
                    disabled={phase === 'sending'}
                    onClick={() => setAttach(a => !a)}>
                    <span className={styles.knob} aria-hidden />
                  </button>
                </div>
                {attach && (
                  <button
                    type="button"
                    className={styles.disclose}
                    aria-expanded={showIncluded}
                    onClick={() => setShowIncluded(s => !s)}>
                    What&rsquo;s included {showIncluded ? '▾' : '▸'}
                  </button>
                )}
                {attach && showIncluded && (
                  <div className={styles.ctxPanel}>
                    <div className={styles.chips}>
                      {props.whereLabel && (
                        <span className={styles.chip}>{props.whereLabel}</span>
                      )}
                      <span className={styles.chip}>Active objective</span>
                      <span className={styles.chip}>VM status</span>
                      <span className={styles.chip}>Content version</span>
                    </div>
                    <p className={styles.reassure}>
                      Passwords, tokens, and keys are removed automatically
                      before anything is sent.
                    </p>
                  </div>
                )}
              </>
            )}

            {phase === 'failed' && (
              <div className={styles.errBanner} role="alert">
                <strong>Couldn&rsquo;t send your report.</strong> Check your
                connection and try again — your message is saved right here.
              </div>
            )}

            <footer className={styles.foot}>
              <button
                className={styles.ghostBtn}
                type="button"
                disabled={phase === 'sending'}
                onClick={props.onClose}>
                Cancel
              </button>
              <button
                className={styles.primaryBtn}
                type="button"
                disabled={!canSend}
                aria-busy={phase === 'sending' || undefined}
                onClick={() => void send()}>
                {phase === 'sending' && (
                  <span className={styles.spinner} aria-hidden />
                )}
                {phase === 'sending'
                  ? 'Sending…'
                  : phase === 'failed'
                    ? 'Try again'
                    : 'Send report'}
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  )
}
